/**
 * §8's Search row: the filter set, the values that actually work, and what an operator in `q` does
 * to the rest of them.
 *
 * This is core rather than screen code for the usual reason — Android needs the identical set — and
 * for one more that is specific to search: **the API accepts every filter it does not understand.**
 * `features=notathing`, `type=bogus` and `page=abc` all answer `200`. There is no server-side
 * validation to lean on at all, so "validate client-side" (§8) is not belt-and-braces, it is the
 * only validation that exists. A value that reaches the wire unchecked comes back looking like a
 * result.
 *
 * Everything here was verified against the live instance on 2026-07-29 by comparing *results*, never
 * by reading a status — and that method needed a control of its own, which is worth recording
 * because it changes what any future check can conclude:
 *
 * ```
 * GET /api/v1/search?q=nature&type=video   ×3, seconds apart
 *   → run1 vs run2: 7 of 20 ids in common      (the ranking is not stable)
 *   → run2 vs run3: byte-identical             (and then it is served from a cache)
 * ```
 *
 * So on this endpoint "the answer changed" proves **nothing**, and only a *self-verifying* check
 * does: `features=live` → 20 of 20 rows `liveNow`, `features=subtitles` → 20 of 20 `hasCaptions`,
 * `features=4k` → 20 of 20 `is4k`, `duration=short` → every row under 240 s, `date=hour` → every row
 * under 0.5 days old. Those are the measurements the lists below rest on.
 */

/**
 * §8 lists six `type` values. **Five ship**, and the sixth is the reason this file validates rather
 * than trusts the documented set: `type=show` is *broken on this instance*, in three different ways
 * depending on the query.
 *
 * ```
 * type=show  ×10 queries
 *   7 × 400 {"error":"Missing hash key: \"primaryContents\"","errorBacktrace":"… (KeyError)"}
 *   2 × 200 []                       (linux, blender open movie)
 *   1 × 200 [{type:"channel"}]       (bluey — a channel, not a show)
 * ```
 *
 * Not once did it return a show. The original plan accepted it because it once answered `200`,
 * which §2's whole first lesson says is not evidence. `movie` stays, because it does work — it
 * answers 11 rows of `type:"video"`, so it needs no new record kind either.
 */
export type SearchType = 'all' | 'video' | 'channel' | 'playlist' | 'movie';

/**
 * The parameter is `sort` and these are the only two values, re-verified. `sort_by` is the correct
 * spelling for **comments** and is silently ignored here — the single most expensive confusion in
 * this API, because both spellings answer `200`.
 */
export type SearchSort = 'relevance' | 'views';

/** `any` is ours: it means "send no `date`", which is not the same as sending an empty one. */
export type SearchDate = 'any' | 'hour' | 'today' | 'week' | 'month' | 'year';

/**
 * YouTube's three windows, and the measurement that pins them: `short` → every row under 240 s,
 * `medium` → 240–507 s, `long` → 2 183 s and up. So the boundaries are 4 and 20 minutes, and a menu
 * may say so.
 */
export type SearchDuration = 'any' | 'short' | 'medium' | 'long';

/**
 * The ten official values. `cc` and `livestream` are documented aliases of `creative_commons` and
 * `live` — accepted on the wire and measurably equivalent, but not offered: two names for one filter
 * in a menu is a bug waiting to be reported.
 *
 * `purchased` and `location` answer `200 []` for every query tried. Kept, because an empty result is
 * a legitimate answer to "paid content matching *nature*" and the page says "no results" rather than
 * pretending; dropping them would be deciding that YouTube has no such videos anywhere.
 */
export type SearchFeature =
  | 'hd'
  | 'subtitles'
  | 'creative_commons'
  | '3d'
  | 'live'
  | 'purchased'
  | '4k'
  | '360'
  | 'location'
  | 'hdr';

export const SEARCH_TYPES: readonly SearchType[] = ['all', 'video', 'channel', 'playlist', 'movie'];
export const SEARCH_SORTS: readonly SearchSort[] = ['relevance', 'views'];
export const SEARCH_DATES: readonly SearchDate[] = ['any', 'hour', 'today', 'week', 'month', 'year'];
export const SEARCH_DURATIONS: readonly SearchDuration[] = ['any', 'short', 'medium', 'long'];
export const SEARCH_FEATURES: readonly SearchFeature[] = [
  'hd',
  'subtitles',
  'creative_commons',
  '3d',
  'live',
  'purchased',
  '4k',
  '360',
  'location',
  'hdr',
];

/**
 * One search, in full. Everything the URL carries — because a results page has to be shareable, and
 * the back button has to return to the filters that produced what you were looking at.
 *
 * `region` is deliberately **not** here. It is a device preference (the same one Trending reads), not
 * a property of the search someone typed, so it is passed at the call site and never written into a
 * link. A shared URL then means "these results, in your region", which is the same contract Trending
 * already has.
 */
export interface SearchQuery {
  readonly q: string;
  readonly type: SearchType;
  readonly sort: SearchSort;
  readonly date: SearchDate;
  readonly duration: SearchDuration;
  readonly features: readonly SearchFeature[];
  /** 1-based, as the API counts. See {@link SEARCH_FIRST_PAGE} for why there is no upper bound. */
  readonly page: number;
}

export const SEARCH_FIRST_PAGE = 1;

export const DEFAULT_SEARCH_QUERY: SearchQuery = {
  q: '',
  type: 'all',
  sort: 'relevance',
  date: 'any',
  duration: 'any',
  features: [],
  page: SEARCH_FIRST_PAGE,
};

function oneOf<T extends string>(values: readonly T[], raw: string | null, fallback: T): T {
  if (raw === null) return fallback;
  const found = values.find((value) => value === raw);
  return found ?? fallback;
}

export function normalizeSearchType(raw: string | null): SearchType {
  return oneOf(SEARCH_TYPES, raw, 'all');
}

export function normalizeSearchSort(raw: string | null): SearchSort {
  return oneOf(SEARCH_SORTS, raw, 'relevance');
}

export function normalizeSearchDate(raw: string | null): SearchDate {
  return oneOf(SEARCH_DATES, raw, 'any');
}

export function normalizeSearchDuration(raw: string | null): SearchDuration {
  return oneOf(SEARCH_DURATIONS, raw, 'any');
}

/**
 * A comma list, filtered against the ten known values and **deduplicated in the canonical order** —
 * so `hdr,hd,hdr` and `hd,hdr` are one query with one cache entry and one URL, rather than three.
 * Unknown entries are dropped silently: they are someone else's URL, not an error to report at the
 * reader.
 */
export function normalizeSearchFeatures(raw: string | null): readonly SearchFeature[] {
  if (raw === null || raw === '') return [];
  const wanted = new Set(raw.split(',').map((part) => part.trim()));
  return SEARCH_FEATURES.filter((feature) => wanted.has(feature));
}

/**
 * 1-based, and **without a ceiling on purpose.** Deep paging fails, but not at a fixed depth:
 * `q=lofi` answered `200` at page 30 and `400` at 35, while `q=music` answered `400` at 30 and `200`
 * again at 60. It is YouTube's continuation running out, per query and not reproducibly, so a
 * constant here would be a guess that is wrong in both directions. The failure is handled where it
 * lands instead — see `isSearchPagingExhausted`.
 */
export function normalizeSearchPage(raw: string | null): number {
  if (raw === null || !/^\d+$/.test(raw)) return SEARCH_FIRST_PAGE;
  const page = Number(raw);
  if (!Number.isSafeInteger(page) || page < SEARCH_FIRST_PAGE) return SEARCH_FIRST_PAGE;
  return page;
}

/* ------------------------------------------------------------------------------------------------
 * `q`-embedded operators
 * ---------------------------------------------------------------------------------------------- */

/**
 * The `key:value` operators YouTube's own search language defines and Invidious forwards.
 *
 * `subscriptions` is here even though it can never work through this endpoint, because the whole
 * point of detecting it is to *say so*: the API route never passes a user into the query processor,
 * so `q=subscriptions:true linux` answers `200 []` — an empty list, which reads as "nothing matched"
 * when the truth is "this cannot be asked here".
 */
export type SearchOperator = 'channel' | 'user' | 'subscriptions';

const OPERATOR_PREFIXES: Readonly<Record<SearchOperator, string>> = {
  channel: 'channel:',
  user: 'user:',
  subscriptions: 'subscriptions:',
};

export interface SearchOperatorReading {
  /** In the order they appear in {@link OPERATOR_PREFIXES}, not in the order they were typed. */
  readonly operators: readonly SearchOperator[];
  /**
   * `type`, `sort`, `date`, `duration` and `features` must be forced back to their defaults before
   * this query goes on the wire. `region` and `page` are unaffected — measured, see below.
   */
  readonly filtersSuppressed: boolean;
  /**
   * The UCID a `channel:` operator names, when it looks like one — so a page that has to explain the
   * suppression has somewhere to send the reader.
   *
   * `null` for `user:` and `subscriptions:`, and for a `channel:` value that is not UCID-shaped. A
   * `channel:` value is not validated by the API at all, and offering a link built from an arbitrary
   * token would be the "byline that navigates to Nothing here" mistake `channelRouteOf` exists to
   * prevent — the difference being that here the alternative is simply not offering the link.
   */
  readonly channelId: string | null;
}

/**
 * Which operators are in this `q`, read the way Invidious reads them.
 *
 * The rule is narrow and it was measured, not assumed — Invidious splits `q` on whitespace and tests
 * each token with Crystal's case-sensitive `starts_with?`, so all three of these are **plain text**:
 *
 * ```
 * channel:UCsXVk…  black hole   → 28 rows, all 28 from that channel     ✓ operator
 * Channel:UCsXVk…  black hole   → 20 rows from 4 channels               ✗ plain text
 * CHANNEL:UCsXVk…  black hole   → 20 rows from 3 channels               ✗ plain text
 * channel: UCsXVk… black hole   → 20 rows from 6 channels               ✗ plain text
 * black hole channel:UCsXVk…    → 28 rows, all 28 from that channel     ✓ position is free
 * ```
 *
 * Matching case-insensitively "to be safe" would therefore be the unsafe choice: it would grey out
 * the filter controls for a query where the filters work perfectly, and silently drop the reader's
 * `duration=short`. The prefix is copied from the wire, exactly.
 *
 * A bare `channel:` with nothing after it is not an operator either — `q=channel:UCsXVk…` *alone*
 * (no search term) answers `200 []`, so the value has to be there and so does a term.
 */
export function readSearchOperators(q: string): SearchOperatorReading {
  const tokens = q.split(/\s+/).filter((token) => token !== '');
  const operators: SearchOperator[] = [];
  let channelId: string | null = null;

  for (const operator of ['channel', 'user', 'subscriptions'] as const) {
    const prefix = OPERATOR_PREFIXES[operator];
    const token = tokens.find((candidate) => candidate.startsWith(prefix) && candidate.length > prefix.length);
    if (token === undefined) continue;
    operators.push(operator);
    if (operator === 'channel') {
      const value = token.slice(prefix.length);
      // UCID-shaped only. This was measured: `channel:` delegates to YouTube's own channel-search
      // path, and `/api/v1/channels/{ucid}/search?q=` returns exactly the same rows —
      // so the channel page is the honest destination for a query the filter bar cannot serve. But a
      // channel that does not exist answers `500 {"error":"Could not get channel info."}`, which is the
      // page reporting an instance failure about a link the app invented.
      channelId = /^UC[A-Za-z0-9_-]{22}$/.test(value) ? value : null;
    }
  }

  return { operators, filtersSuppressed: operators.length > 0, channelId };
}

/**
 * The query as it will actually be sent — with a suppressed filter reset to its default rather than
 * left set-but-ignored.
 *
 * **This is the correction that matters, and the API reference had it backwards.** It recorded that
 * "when `channel:`/`user:`/`subscriptions:` appears in `q`, every other filter is silently ignored".
 * Ignored would be harmless. Measured on two channels, what actually happens is that a non-default
 * filter *contaminates* the result while it still looks like a channel search:
 *
 * ```
 * q=channel:UCsXVk… black hole             → 28 rows, 1 author,  28 from the channel   ✓
 *   &type=all                              → 28 rows, 1 author,  28 from the channel   ✓ default is safe
 *   &sort=relevance                        → 28 rows, 1 author,  28 from the channel   ✓ default is safe
 *   &region=DE                             → 28 rows, 1 author,  28 from the channel   ✓ unaffected
 *   &page=2                                → 30 rows, 1 author,  30 from the channel   ✓ unaffected
 *   &type=video                            → 20 rows, 6 authors, 15 from the channel   ✗
 *   &features=hd                           → 19 rows, 4 authors, 16 from the channel   ✗
 *   &date=year                             → 10 rows, 4 authors,  7 from the channel   ✗
 *   &duration=long                         → 19 rows, 10 authors, 1 from the channel   ✗
 *   &sort=views                            →  6 rows, 5 authors,  1 from the channel   ✗
 * ```
 *
 * So the reader would get a page titled "in this channel" holding other people's videos, or — with
 * `sort=views` on the first channel probed — a reproducible `200 []` that reads as "this channel has
 * nothing like that". §8's "disable and grey the other controls with the reason attached" is
 * therefore not a nicety about a no-op; it is the only way the page can be truthful. And because the
 * defaults are safe, resetting is a real fix rather than a refusal.
 */
export function effectiveSearchQuery(query: SearchQuery): SearchQuery {
  if (!readSearchOperators(query.q).filtersSuppressed) return query;
  return {
    ...query,
    type: DEFAULT_SEARCH_QUERY.type,
    sort: DEFAULT_SEARCH_QUERY.sort,
    date: DEFAULT_SEARCH_QUERY.date,
    duration: DEFAULT_SEARCH_QUERY.duration,
    features: DEFAULT_SEARCH_QUERY.features,
  };
}

/** How many filters this query sets away from their defaults — for a "clear filters" affordance. */
export function activeSearchFilterCount(query: SearchQuery): number {
  let count = 0;
  if (query.type !== DEFAULT_SEARCH_QUERY.type) count += 1;
  if (query.sort !== DEFAULT_SEARCH_QUERY.sort) count += 1;
  if (query.date !== DEFAULT_SEARCH_QUERY.date) count += 1;
  if (query.duration !== DEFAULT_SEARCH_QUERY.duration) count += 1;
  count += query.features.length;
  return count;
}

/**
 * The wire parameters for a search, defaults omitted.
 *
 * Omitted rather than sent-as-default, even though the defaults were measured safe next to an
 * operator: a URL that carries only what was asked for is one a human can read in a network log,
 * and every parameter that is not there is one that cannot be honoured wrongly.
 *
 * `region` comes from the caller (a device preference, not part of the query) and is expected to be
 * validated already — `normalizeContentRegion` in `feed/region.ts`. Search is more forgiving than
 * Trending here (an unsupported code answers `200` rather than Trending's leaked `KeyError` 500), but
 * validating both the same way means one rule to remember instead of two.
 */
export function searchWireParams(
  query: SearchQuery,
  options: { readonly region?: string | null } = {},
): Readonly<Record<string, string | null>> {
  const effective = effectiveSearchQuery(query);
  return {
    q: effective.q,
    type: effective.type === 'all' ? null : effective.type,
    sort: effective.sort === 'relevance' ? null : effective.sort,
    date: effective.date === 'any' ? null : effective.date,
    duration: effective.duration === 'any' ? null : effective.duration,
    features: effective.features.length === 0 ? null : effective.features.join(','),
    region: options.region ?? null,
    page: effective.page === SEARCH_FIRST_PAGE ? null : String(effective.page),
  };
}

/** Nothing to search for. A blank `q` answers `200 []`, so this is a decision, not a guard. */
export function isBlankSearch(query: SearchQuery): boolean {
  return query.q.trim() === '';
}
